import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, LineChart, TrendingUp, Briefcase, Award } from 'lucide-react';
import { services, whyChooseUs } from '../utils/mockData';

const Home = () => {
  const iconMap = {
    LineChart, TrendingUp, Briefcase, Award
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="pt-40 pb-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-6 lg:px-16">
          <div className="max-w-5xl">
            <div className="inline-block px-4 py-2 bg-amber-50 border border-amber-200 rounded-full mb-8">
              <span className="text-sm font-medium text-amber-800">Est. 2025 • Dubai, UAE</span>
            </div>
            <h1 className="text-6xl md:text-7xl font-serif font-bold text-gray-900 mb-8 leading-tight">
              Strategic Marketing for International Growth
            </h1>
            <p className="text-xl text-gray-600 mb-10 leading-relaxed max-w-3xl">
              ETAN provides structured marketing strategies and business consulting services to European and international clients seeking measurable, sustainable growth.
            </p>
            <Link to="/contact">
              <Button 
                className="bg-amber-600 hover:bg-amber-700 text-white px-10 h-14 text-base font-medium tracking-wide transition-colors duration-200"
              >
                Request Consultation
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-24 bg-white border-y border-gray-100">
        <div className="container mx-auto px-6 lg:px-16">
          <div className="max-w-4xl">
            <p className="text-xl text-gray-700 leading-relaxed">
              Established in 2025, ETAN works with businesses across the European Union and international markets, providing clear, data-driven marketing and consulting services focused on measurable results.
            </p>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-28 bg-gray-50">
        <div className="container mx-auto px-6 lg:px-16">
          <div className="mb-20">
            <h2 className="text-5xl font-serif font-bold text-gray-900 mb-6">
              Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl">
              Practical, structured solutions tailored to each client's business goals and market environment.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 max-w-6xl">
            {services.map((service) => {
              const Icon = iconMap[service.icon];
              return (
                <div 
                  key={service.id} 
                  className="group"
                >
                  <div className="flex items-start space-x-6">
                    <div className="w-16 h-16 bg-amber-600 flex items-center justify-center flex-shrink-0 group-hover:bg-amber-700 transition-colors duration-200">
                      <Icon className="text-white" size={28} />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-serif font-semibold text-gray-900 mb-4">
                        {service.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed text-lg">
                        {service.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-16">
            <Link to="/services">
              <Button 
                variant="outline" 
                className="border-2 border-gray-300 text-gray-900 hover:border-gray-400 hover:bg-gray-50 px-8 h-12 font-medium tracking-wide"
              >
                View All Services
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* International Focus */}
      <section className="py-28 bg-white">
        <div className="container mx-auto px-6 lg:px-16">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <div>
              <h2 className="text-5xl font-serif font-bold text-gray-900 mb-8">
                International Expertise
              </h2>
              <p className="text-xl text-gray-700 leading-relaxed mb-6">
                ETAN primarily works with clients across the European Union, providing services adapted to international markets, regulatory environments, and cross-border business operations.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Our experience spans multiple jurisdictions, enabling us to navigate complex international business landscapes effectively.
              </p>
            </div>
            <div className="bg-gray-900 p-12 text-white">
              <h3 className="text-3xl font-serif font-semibold mb-6">Key Markets</h3>
              <ul className="space-y-4 text-lg">
                <li className="flex items-start">
                  <span className="text-amber-500 mr-3">•</span>
                  <span>European Union</span>
                </li>
                <li className="flex items-start">
                  <span className="text-amber-500 mr-3">•</span>
                  <span>Middle East</span>
                </li>
                <li className="flex items-start">
                  <span className="text-amber-500 mr-3">•</span>
                  <span>International Markets</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-28 bg-gray-50">
        <div className="container mx-auto px-6 lg:px-16">
          <div className="mb-20">
            <h2 className="text-5xl font-serif font-bold text-gray-900 mb-6">
              Why Work With ETAN
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-x-20 gap-y-12 max-w-6xl">
            {whyChooseUs.map((item, index) => (
              <div key={index} className="border-l-4 border-amber-600 pl-8">
                <h3 className="text-2xl font-serif font-semibold text-gray-900 mb-4">
                  {item.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-lg">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-28 bg-gray-900">
        <div className="container mx-auto px-6 lg:px-16">
          <div className="max-w-4xl">
            <h2 className="text-5xl font-serif font-bold text-white mb-8">
              Ready to Discuss Your Project?
            </h2>
            <p className="text-2xl text-gray-300 mb-10 leading-relaxed">
              Contact us to schedule a consultation and explore how ETAN can support your business objectives.
            </p>
            <Link to="/contact">
              <Button 
                className="bg-amber-600 hover:bg-amber-700 text-white px-10 h-14 text-base font-medium tracking-wide transition-colors duration-200"
              >
                Get in Touch
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;